import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { getWeekDateRange } from '../utils/timeUtils';
import { getWeekNumber, getWeekStartDate, formatWeekLabel } from '../utils/helpers.ts';
import WeekSelectionModal from './WeekSelectionModal';
import HelpIcon from './Documentation/HelpIcon';
import { TIME_FRAME_SINGLE_WEEK, TIME_FRAME_WHOLE_PERIOD } from '../utils/timelineViewRange';

const RANGE_WHOLE_PERIOD = TIME_FRAME_WHOLE_PERIOD;
const RANGE_SINGLE_WEEK = TIME_FRAME_SINGLE_WEEK;

const ClassSchedulerTimeFrameSelector = ({
    timePeriods,
    selectedPeriodId,
    onMonthChange,
    onWeekChange,
    selectedWeek,
    selectedMonth,
    currentView,
    collapsedPeriodSelect = null,
    selectedRange = RANGE_WHOLE_PERIOD,
    onRangeChange
}) => {
    const [isWeekModalOpen, setIsWeekModalOpen] = useState(false);

    const selectedPeriod = useMemo(
        () => timePeriods.find(p => String(p.id) === String(selectedPeriodId)) ?? null,
        [timePeriods, selectedPeriodId]
    );

    const calendarYearForPeriod = selectedPeriod?.year ?? new Date().getFullYear();

    const periodWeekOptions = useMemo(() => {
        if (
            !selectedPeriod ||
            typeof selectedPeriod.start_week !== 'number' ||
            typeof selectedPeriod.end_week !== 'number'
        ) {
            return [];
        }

        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        const periodYear = calendarYearForPeriod;
        const weeks = [];

        const pushWeek = (week, year) => {
            const weekDateRange = getWeekDateRange(year, week);
            const dateLabel = weekDateRange
                ? `${weekDateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : '';

            weeks.push({
                week,
                year,
                label: `Week ${week} (${dateLabel})`
            });
        };

        if (periodStart <= periodEnd) {
            for (let week = periodStart; week <= periodEnd; week++) {
                pushWeek(week, periodYear);
            }
        } else {
            for (let week = periodStart; week <= 53; week++) {
                pushWeek(week, periodYear);
            }
            for (let week = 1; week <= periodEnd; week++) {
                pushWeek(week, periodYear + 1);
            }
        }

        return weeks;
    }, [selectedPeriod, calendarYearForPeriod]);

    const weekInPeriod = useCallback((week, year) => {
        if (!selectedPeriod) return false;
        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        const periodYear = calendarYearForPeriod;

        if (periodStart <= periodEnd) {
            return year === periodYear && week >= periodStart && week <= periodEnd;
        }
        return (year === periodYear && week >= periodStart) || (year === periodYear + 1 && week <= periodEnd);
    }, [selectedPeriod, calendarYearForPeriod]);

    const getDefaultWeekForPeriod = useCallback(() => {
        if (!selectedPeriod) return null;

        const now = new Date();
        let week = getWeekNumber(now);
        let year = now.getFullYear();

        if (!weekInPeriod(week, year)) {
            const first = periodWeekOptions[0];
            return first ? { week: first.week, year: first.year } : null;
        }

        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        if (periodStart > periodEnd) {
            year = week <= periodEnd ? calendarYearForPeriod + 1 : calendarYearForPeriod;
        } else {
            year = calendarYearForPeriod;
        }

        return { week, year };
    }, [selectedPeriod, weekInPeriod, periodWeekOptions, calendarYearForPeriod]);

    const emitRangeChange = useCallback((range) => {
        if (typeof onRangeChange === 'function') {
            onRangeChange(range);
        }
    }, [onRangeChange]);

    const handleRangeChange = (range) => {
        if (currentView === 'room') return;

        emitRangeChange(range);

        if (onMonthChange) onMonthChange(null);

        if (range === RANGE_WHOLE_PERIOD) {
            if (onWeekChange) onWeekChange(null);
            return;
        }

        const weekToApply = (selectedWeek && weekInPeriod(selectedWeek.week, selectedWeek.year))
            ? selectedWeek
            : getDefaultWeekForPeriod();
        if (weekToApply && onWeekChange) {
            onWeekChange(weekToApply);
        }
    };

    const selectedWeekIndex = useMemo(() => {
        if (!selectedWeek) return -1;
        return periodWeekOptions.findIndex(
            (opt) => opt.week === selectedWeek.week && opt.year === selectedWeek.year
        );
    }, [selectedWeek, periodWeekOptions]);

    const navigateWeek = useCallback((direction) => {
        if (!onWeekChange || periodWeekOptions.length === 0) return;

        let nextIndex = selectedWeekIndex;
        if (nextIndex === -1) {
            nextIndex = direction > 0 ? 0 : periodWeekOptions.length - 1;
        } else {
            nextIndex = (nextIndex + direction + periodWeekOptions.length) % periodWeekOptions.length;
        }

        const next = periodWeekOptions[nextIndex];
        onWeekChange({ week: next.week, year: next.year });
    }, [onWeekChange, periodWeekOptions, selectedWeekIndex]);

    const currentWeekDate = useMemo(() => {
        if (!selectedWeek) return null;
        const range = getWeekDateRange(selectedWeek.year, selectedWeek.week);
        return range?.startDate ?? null;
    }, [selectedWeek]);

    const currentWeekLabel = useMemo(() => {
        if (!currentWeekDate) return 'Select week';
        return formatWeekLabel(getWeekStartDate(currentWeekDate));
    }, [currentWeekDate]);

    const longestWeekDateLabel = useMemo(() => {
        return periodWeekOptions.reduce((longest, option) => {
            const range = getWeekDateRange(option.year, option.week);
            if (!range?.startDate) return longest;
            const label = formatWeekLabel(getWeekStartDate(range.startDate));
            return label.length > longest.length ? label : longest;
        }, 'Select week');
    }, [periodWeekOptions]);

    const handleWeekSelectFromModal = useCallback((selectedDate) => {
        if (!onWeekChange || !selectedDate) return;

        const week = getWeekNumber(selectedDate);
        const year = selectedDate.getFullYear();

        if (!weekInPeriod(week, year)) return;

        let resolvedYear = year;
        if (selectedPeriod && selectedPeriod.start_week > selectedPeriod.end_week) {
            resolvedYear = week <= selectedPeriod.end_week ? calendarYearForPeriod + 1 : calendarYearForPeriod;
        } else if (selectedPeriod) {
            resolvedYear = calendarYearForPeriod;
        }

        onWeekChange({ week, year: resolvedYear });
    }, [onWeekChange, weekInPeriod, selectedPeriod, calendarYearForPeriod]);

    useEffect(() => {
        if (currentView === 'room') {
            emitRangeChange(RANGE_WHOLE_PERIOD);
            if (onMonthChange) onMonthChange(null);
            if (onWeekChange) onWeekChange(null);
        }
    }, [currentView, emitRangeChange, onMonthChange, onWeekChange]);

    useEffect(() => {
        if (selectedMonth && onMonthChange) {
            onMonthChange(null);
        }
    }, [selectedMonth, onMonthChange]);

    const isRoomView = currentView === 'room';
    const showWeekNavigator = selectedRange === RANGE_SINGLE_WEEK && !isRoomView;

    const rangeButtonClass = (isActive) => (
        `flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${isActive
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-800'
        } ${isRoomView ? 'cursor-not-allowed opacity-60' : ''}`
    );

    return (
        <>
            <div className="flex items-center gap-2 min-h-[2rem]">
                <div className="flex flex-none items-center gap-2">
                    <div className="flex items-stretch gap-0.5 bg-white/80 rounded-lg p-0.5 shadow-sm ring-1 ring-gray-200/80">
                        <button
                            type="button"
                            onClick={() => handleRangeChange(RANGE_WHOLE_PERIOD)}
                            disabled={isRoomView}
                            className={rangeButtonClass(selectedRange === RANGE_WHOLE_PERIOD)}
                        >
                            Whole Period
                        </button>
                        <button
                            type="button"
                            onClick={() => handleRangeChange(RANGE_SINGLE_WEEK)}
                            disabled={isRoomView || periodWeekOptions.length === 0}
                            className={rangeButtonClass(selectedRange === RANGE_SINGLE_WEEK)}
                            title={periodWeekOptions.length === 0 ? 'Select a period on the timeline first' : undefined}
                        >
                            Single Week
                        </button>
                    </div>
                    <HelpIcon documentationId="class-scheduler-time-frame" position="relative" size={14} />
                    {showWeekNavigator && (
                        <div className="flex items-center">
                            <button
                                type="button"
                                onClick={() => navigateWeek(-1)}
                                className="flex w-6 flex-none items-center justify-center text-gray-400 transition-colors hover:text-blue-600"
                                title="Previous week"
                                aria-label="Previous week"
                            >
                                <IoChevronBack className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsWeekModalOpen(true)}
                                className="relative flex flex-col items-center justify-center px-1 py-0.5 text-center"
                                title={selectedWeek
                                    ? `Week ${selectedWeek.week} — ${currentWeekLabel}. Select week`
                                    : 'Select week'}
                                aria-label={selectedWeek
                                    ? `Week ${selectedWeek.week}, ${currentWeekLabel}. Select week`
                                    : 'Select week'}
                            >
                                <span className="invisible whitespace-nowrap text-sm font-medium leading-tight tabular-nums" aria-hidden="true">
                                    Week 53
                                </span>
                                <span className="invisible whitespace-nowrap text-[11px] font-medium leading-tight" aria-hidden="true">
                                    {longestWeekDateLabel}
                                </span>
                                <span className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-sm font-medium leading-tight text-blue-600 tabular-nums">
                                        {selectedWeek ? `Week ${selectedWeek.week}` : 'Week'}
                                    </span>
                                    <span className="whitespace-nowrap text-[11px] font-medium leading-tight text-gray-500">
                                        {currentWeekLabel}
                                    </span>
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => navigateWeek(1)}
                                className="flex w-6 flex-none items-center justify-center text-gray-400 transition-colors hover:text-blue-600"
                                title="Next week"
                                aria-label="Next week"
                            >
                                <IoChevronForward className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex min-w-0 flex-1 items-center justify-center">
                    {collapsedPeriodSelect}
                </div>
            </div>

            <WeekSelectionModal
                isOpen={isWeekModalOpen}
                onClose={() => setIsWeekModalOpen(false)}
                currentDate={currentWeekDate}
                onWeekSelect={(selectedDate) => {
                    handleWeekSelectFromModal(selectedDate);
                    setIsWeekModalOpen(false);
                }}
            />
        </>
    );
};

export default ClassSchedulerTimeFrameSelector;
export { RANGE_WHOLE_PERIOD, RANGE_SINGLE_WEEK };
